import { useState } from 'react'
import { faNum } from '../platform/labels'

/**
 * The two charts the dashboard needs, drawn by hand rather than through a
 * library: one-series bars and one-series line, in the accent alone.
 *
 * One series each, so no legend and no categorical palette — the heading
 * names the measure. Every value is also printed or reachable as text, and
 * each chart has a table view beside it, so nothing is carried by colour or by
 * hovering alone.
 */

export interface BarDatum {
  key: string
  labelFa: string
  /** 0–100. */
  percent: number
  /** Printed under the bar: what the number rests on. */
  noteFa?: string
}

/**
 * Horizontal bars, one per row, filling from the start edge (right, under
 * RTL). `floor` draws the one reference line the standard defines — §6-1's 50%
 * per dimension — so «below the floor» is read off the chart, not computed.
 */
export function PercentBars({ data, floor, floorFa }: { data: BarDatum[]; floor?: number; floorFa?: string }) {
  return (
    <div className="pbars" role="list">
      {data.map((d) => (
        <div key={d.key} className="pbars__row" role="listitem" title={`${d.labelFa}: ${faNum(d.percent)}٪`}>
          <div className="pbars__label">{d.labelFa}</div>
          <div className="pbars__track">
            <div className="pbars__fill" style={{ width: `${Math.min(Math.max(d.percent, 0), 100)}%` }} />
            {floor !== undefined && <div className="pbars__floor" style={{ insetInlineStart: `${floor}%` }} aria-hidden="true" />}
          </div>
          <div className="pbars__value num">{faNum(d.percent)}٪</div>
          {d.noteFa && <div className="pbars__note">{d.noteFa}</div>}
        </div>
      ))}
      {floor !== undefined && floorFa && (
        <div className="pbars__legend">
          <span className="pbars__legend-mark" aria-hidden="true" /> {floorFa}
        </div>
      )}
    </div>
  )
}

export interface LinePoint {
  on: string
  value: number
  noteFa?: string
}

const jalali = (iso: string) =>
  new Intl.DateTimeFormat('fa-IR-u-ca-persian', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`))

/**
 * A single line over time, 0–100 on one axis.
 *
 * Time runs left to right even under RTL, as Persian charts conventionally
 * draw it; the labels around it stay Persian. A crosshair follows the pointer
 * and names the nearest day, and the points are shown as markers when there
 * are few enough to read.
 */
export function TrendChart({ points, labelFa }: { points: LinePoint[]; labelFa: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 640
  const H = 220
  const pad = { top: 12, right: 16, bottom: 28, left: 36 }
  const iw = W - pad.left - pad.right
  const ih = H - pad.top - pad.bottom

  const times = points.map((p) => Date.parse(`${p.on}T12:00:00Z`))
  const t0 = Math.min(...times)
  const t1 = Math.max(...times)
  const x = (t: number) => pad.left + (t1 === t0 ? iw / 2 : ((t - t0) / (t1 - t0)) * iw)
  const y = (v: number) => pad.top + ih - (Math.min(Math.max(v, 0), 100) / 100) * ih
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(times[i] ?? 0).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * W
    let best = 0
    for (let i = 1; i < points.length; i += 1) {
      if (Math.abs(x(times[i] ?? 0) - px) < Math.abs(x(times[best] ?? 0) - px)) best = i
    }
    setHover(best)
  }

  const active = hover === null ? null : points[hover]
  const showMarkers = points.length <= 24

  return (
    <figure className="trend">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="trend__svg"
        role="img"
        aria-label={labelFa}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={pad.left} x2={W - pad.right} y1={y(v)} y2={y(v)} className="trend__grid" />
            <text x={pad.left - 6} y={y(v) + 4} className="trend__tick" textAnchor="end">
              {faNum(v)}
            </text>
          </g>
        ))}
        <path d={path} className="trend__line" />
        {showMarkers &&
          points.map((p, i) => <circle key={p.on} cx={x(times[i] ?? 0)} cy={y(p.value)} r={4} className="trend__dot" />)}
        {points.length > 0 && (
          <>
            <text x={pad.left} y={H - 8} className="trend__tick" textAnchor="start">
              {jalali(points[0]?.on ?? '')}
            </text>
            <text x={W - pad.right} y={H - 8} className="trend__tick" textAnchor="end">
              {jalali(points.at(-1)?.on ?? '')}
            </text>
          </>
        )}
        {active && hover !== null && (
          <g>
            <line x1={x(times[hover] ?? 0)} x2={x(times[hover] ?? 0)} y1={pad.top} y2={pad.top + ih} className="trend__cross" />
            <circle cx={x(times[hover] ?? 0)} cy={y(active.value)} r={5} className="trend__dot trend__dot--active" />
          </g>
        )}
      </svg>
      <figcaption className="trend__tip" aria-live="polite">
        {active ? (
          <>
            {jalali(active.on)}: <span className="num">{faNum(active.value)}٪</span>
            {active.noteFa && <> · {active.noteFa}</>}
          </>
        ) : (
          <span className="muted">نشانگر را روی نمودار ببرید تا عدد هر روز را ببینید.</span>
        )}
      </figcaption>
    </figure>
  )
}

/** The same data as a table, for anyone who would rather read than hover. */
export function TableToggle({ children, labelFa }: { children: React.ReactNode; labelFa: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <button type="button" className="btn btn--quiet btn--sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'بستن جدول' : labelFa}
      </button>
      {open && <div className="table-scroll" style={{ marginTop: 'var(--space-2)' }}>{children}</div>}
    </div>
  )
}
